/**
 * 44px, two rows of information in one strip (components.md §1).
 *
 * ```
 * BTC │ BTC ▲▼ 1m │ STRIKE $63,418.06 │ +$23.12
 * btc-updown-1m-1786637400 │ UP 63¢  DOWN 37¢ │ VOL $86K │ OI $21K │ ⏱ 34
 * ```
 *
 * The leftmost cell is the asset selector — a segmented control, not a dropdown,
 * with one tab per asset the venue runs (BTC alone today, §F138, §F140): it is
 * what names the market on this page. It is passed in rather than built here.
 *
 * **The `market_id` cell is click-to-copy and that is not a nicety.** At 2,880
 * markets a day, support conversations are impossible without the user being
 * able to name the market they were in. It is `caption`/`.ud-num` in
 * `text-tertiary`, per §1.
 *
 * The `stats` slot takes whatever the market has resolved so far; during
 * `SCHEDULED` the strike is genuinely NULL and the caller renders `—`, which is
 * the honest render rather than a stale number.
 *
 * **Below 768px it is two rows**: the asset switch with the market's state
 * pinned right, then the stats on a row of their own as equal cells that wrap
 * rather than scroll. One row could not hold both — the state was the last
 * thing in a row that scrolled sideways, so the moment the ticker filled the
 * numbers in it went off the right edge, and every stat added later would have
 * pushed it further.
 */

import type { ReactNode } from 'react'
import { useCopy } from '@/app/use-copy'
import { cx } from '@/lib/cx'

export interface MarketStripProps {
  /** The asset `<SegmentedControl>`, one tab per asset the venue runs. */
  assetSelector: ReactNode
  marketId?: string
  /** Strike, index, volume, OI — whatever is resolved. */
  stats?: ReactNode
  /**
   * The market's state — its status pill and whatever qualifies it (the clock
   * warning).
   *
   * **Pinned, never inside the stats' scroll.** It shared that well until
   * 2026-09-11 and was the reading a phone lost first; a trader can do without
   * the open interest and cannot do without knowing whether the market is
   * open.
   */
  status?: ReactNode
  /** The `<Countdown>` and its progress ring, in Phase 4. */
  countdown?: ReactNode
  /**
   * Controls over the page rather than readings from it — the pro switch.
   *
   * The far right of this strip is the only place on the page that belongs to
   * the whole view rather than to one panel, which is what a mode switch needs:
   * the pro ladder replaces two panels, so a control living inside either of
   * them disappears with the thing it switched.
   */
  actions?: ReactNode
  className?: string
}

export function MarketStrip({
  assetSelector,
  marketId,
  stats,
  status,
  countdown,
  actions,
  className,
}: MarketStripProps) {
  return (
    <div
      className={cx(
        // `flex-wrap` and `order-last` rather than a second tree for phones:
        // one DOM, one set of subscriptions, at every width.
        'flex shrink-0 flex-wrap bg-bg-surface px-3',
        'md:h-[var(--ud-subheader-height)] md:flex-nowrap md:items-stretch md:gap-3',
        className,
      )}
    >
      <div className="flex h-[var(--ud-subheader-height)] items-center md:h-auto">{assetSelector}</div>

      {marketId ? (
        <div className="hidden items-center md:flex">
          <MarketIdChip marketId={marketId} />
        </div>
      ) : null}

      <div
        data-testid="market-stats"
        className={cx(
          // Below 768px: a row of its own, equal cells, and a fifth stat wraps
          // to another line instead of scrolling something off screen.
          'ud-scroll order-last grid basis-full grid-cols-[repeat(auto-fit,minmax(calc(72*var(--ud-u)),1fr))] gap-x-2 gap-y-1',
          'border-t border-border-subtle py-1',
          // From 768px: the single scrolling row it has always been.
          'md:order-none md:flex md:min-w-0 md:flex-1 md:basis-auto md:items-center md:gap-4 md:border-t-0 md:py-0',
        )}
      >
        {stats}
      </div>

      {status ? (
        <div data-testid="market-status" className="ml-auto flex shrink-0 items-center gap-2 md:ml-0">
          {status}
        </div>
      ) : null}

      {countdown ? <div className="flex shrink-0 items-center">{countdown}</div> : null}

      {actions ? <div className="flex shrink-0 items-center">{actions}</div> : null}
    </div>
  )
}

/**
 * Click to copy. The confirmation is the label changing to `copied` for a
 * moment — no toast, because copying a market id is not an event worth one of
 * the three toast slots.
 */
export function MarketIdChip({ marketId, className }: { marketId: string; className?: string }) {
  const { copied, copy } = useCopy()

  return (
    <button
      type="button"
      title="copy market id"
      onClick={() => copy(marketId)}
      className={cx(
        'ud-num rounded-sm px-1 text-caption text-text-tertiary transition-colors duration-[var(--ud-duration-instant)]',
        'hover:bg-bg-hover hover:text-text-secondary focus-visible:shadow-focus focus-visible:outline-none',
        className,
      )}
    >
      {copied ? 'copied' : marketId}
    </button>
  )
}
